import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Message } from '@b-design/fusion';
import PaginationComponent from '@/components/PaginationComponent';
import SetAsModal from '@/components/SetAsModal';
import Confirm from '@/components/Confirm';
import $i18n from '@/i18n';
import './index.less';
import { urlEmun, appMemberColumns, renderActionItem } from './const.js';
import Requests from '@/services/request';
import { Divider } from '@alifd/next';
import store from '@/store';
import { getQueryStringArgs } from '@/utils/url-parse';

interface Member {
  roleList: string[];
  contentHeight: number
}

const Member: React.FC<Member> = ({ roleList, contentHeight }) => {
  const [column, setColumns] = useState<any[]>(appMemberColumns);
  const [dataSource, setDataSource] = useState({ list: [], total: 0 });
  const [loading, setLoading] = useState(false);

  // filter 
  const [filterValue, setFilterValue] = useState<{ role?: string | undefined; status?: string | undefined; }>({});

  // 分页参数
  const [paginationValue, setPaginationValue] = useState<
    Record<'currentPage' | 'pageSize', number>
  >({
    currentPage: 1,
    pageSize: 10,
  });

  // Set As弹框
  const [setAsVisible, setSetAsVisible] = useState<boolean>(false);
  // TODO
  // Set As角色选择
  const [setAsRole, setSetAsRole] = useState<string[]>(['ADMIN']);

  // 修改角色时
  const memberInfo = useRef<Record<'accountIdList' | 'authId', string[] | string>>({ accountIdList: [], authId: '' })

  const [userState] = store.useModel('user');
  const { spaceId } = getQueryStringArgs();


  // 获取空间成员
  const queryChannel = useCallback(
    async (params = { authId: spaceId, currentPage: 1, pageSize: 10 }) => {
      if (!spaceId) {
        setDataSource({ list: [], total: 0 });
        return;
      };
      setLoading(true)
      const data = await Requests.get('/rest/intercnn/space/auth/query', { ...params });
      if (data) {
        const { list = [], total  } = data;
        const newList = list.map((item: any) => {
          return {
            ...item,
            roleList: item?.role?.split(',') || [],
          }
        })
        setDataSource({ list: newList, total });
        setLoading(false)
      }
    },
    [spaceId],
  );

  const [searchParams, setSearchParams] = useState({
    filterValue: {},
    currentPage: 1,
    pageSize: 10,
    authId: spaceId,
  })
  useEffect(() => {
    setSearchParams(pre => {
      if (JSON.stringify(pre.filterValue) === JSON.stringify(filterValue)) {
        return {
          filterValue,
          currentPage: paginationValue.currentPage,
          pageSize: paginationValue.pageSize,
          authId: spaceId,
        }
      } else {
        return {
          filterValue,
          currentPage: 1,
          pageSize: paginationValue.pageSize,
          authId: spaceId,
        }
      }
    })
  }, [JSON.stringify({ paginationValue, spaceId, filterValue })]);

  useEffect(() => {
    const { authId, currentPage, pageSize, filterValue } = searchParams
    queryChannel({ authId, currentPage, pageSize, ...filterValue });
  }, [JSON.stringify(searchParams)]);

  const onOpen = useCallback(() => {
    setSetAsVisible(true);
  }, []);

  const onClose = useCallback(() => {
    setSetAsVisible(false);
  }, []);

  const onOk = async (type, params = {}) => {
    const data = await Requests.post(urlEmun?.[type], type === 'add' ? { ...memberInfo?.current, role: setAsRole.join(',') } : { ...params });
    if (data) {
      Message.success({
        title: $i18n.get({
          id: 'aopf.pages.channel.channelList.message.success',
          dm: '修改成功',
        }),
      });
      onClose()
      queryChannel({ authId: spaceId, ...paginationValue, ...filterValue })
    }
  }

  // 根据用户角色处理column
  useEffect(() => {
    const actionRender = {
      title: $i18n.get({ id: 'aopf.pages.member.columnsAction', dm: '操作' }),
      dataIndex: 'action',
      width: 200,
      // lock: true,
      cell: (_v, _i, record) => (
        <div style={{ color: 'var(--superapp-primary-1)', display: 'flex', alignItems: 'center', fontSize: '12px' }}>
          {userState?.currentUser?.accountId === record?.accountId || record?.status === 'BLOCKED' ?
            renderActionItem({
              icon: 'settings--outline',
              content: $i18n.get({
                id: 'aopf.pages.member.columnsActionSetAs',
                dm: '设置',
              }),
              disable: true
            })
            : <div
              onClick={() => {
                onOpen();
                setSetAsRole(record?.roleList);
                memberInfo.current = { accountIdList: [record?.accountId], authId: record?.authId }
              }}
            >
              {renderActionItem({
                icon: 'settings--outline',
                content: $i18n.get({
                  id: 'aopf.pages.member.columnsActionSetAs',
                  dm: '设置',
                }),
              })}
            </div>}
          <Divider direction="ver" />

          {record?.status === 'BLOCKED'
            ?
            <Confirm
              trigger={renderActionItem({
                icon: 'settings--outline',
                content: $i18n.get({
                  id: 'aopf.pages.member.columnsAction.unBlock',
                  dm: '激活',
                }),
              })}
              dialogTitle={$i18n.get({
                id: 'aopf.pages.member.columnsAction.unBlock',
                dm: '激活',
              })}
              dialogContent={$i18n.get({
                id: 'aopf.pages.member.columnsAction.unBlock.message',
                dm: '你确定要激活吗?',
              })}
              width={300}
              onClick={onOk.bind(null, 'unblock', { accountIdList: [record?.accountId], authId: record?.authId, role: record?.roleList?.join(',') })}
            />
            : userState?.currentUser?.accountId === record?.accountId ? renderActionItem({
              icon: 'settings--outline',
              content: $i18n.get({
                id: 'aopf.pages.member.columnsAction.unBlock',
                dm: '激活',
              }),
              disable: true
            }) : <Confirm
              trigger={renderActionItem({
                icon: 'settings--outline',
                content: $i18n.get({
                  id: 'aopf.pages.member.columnsActionBlock',
                  dm: '冻结',
                }),
              })}
              dialogTitle={$i18n.get({
                id: 'aopf.pages.member.columnsActionBlock',
                dm: '冻结',
              })}
              dialogContent={$i18n.get({
                id: 'aopf.pages.member.columnsActionBlock.message',
                dm: '你确定要冻结吗?',
              })}
              width={300}
              onClick={onOk.bind(null, 'disable', { accountId: record?.accountId, authId: record?.authId })}
            />}
        </div>
      ),
    };
    if (roleList?.includes('ADMIN') || roleList?.includes('AIGC_DESIGNER')) {
      setColumns([...appMemberColumns, actionRender]);
    } else {
      setColumns([...appMemberColumns,]);
    }
  }, [roleList, onOk]);

  return (
    <div>
      <div className="list-content">
        <Table.StickyLock columns={column} dataSource={dataSource?.list}
          fixedHeader
          maxBodyHeight={contentHeight - 48}
          loading={loading}
          onFilter={(filterParms) => {
            const filter: any = {};
            for (const [key, value] of Object.entries(filterParms)) {
              filter[key] = (value as any)?.selectedKeys?.[0]
            }
            if(filterParms?.roleList) {
              filter.role = filter.roleList;
            }
            setPaginationValue((pre) => ({
              ...pre,
              currentPage: 1,
            }));
            setFilterValue(pre => ({ ...pre, ...filter }))
          }} />
        <div className='pagenation-container'>
          <PaginationComponent
            total={dataSource?.total}
            current={paginationValue?.currentPage}
            pageSize={paginationValue?.pageSize}
            onPageChange={(current, pageSize) => {
              setPaginationValue({
                currentPage: current,
                pageSize,
              });
            }}
          />
        </div>
      </div>
      {/* 设置弹框 */}
      <SetAsModal
        setAsVisible={setAsVisible}
        setSetAsRole={setSetAsRole}
        setAsRole={setAsRole}
        onClose={onClose}
        onOk={onOk.bind(null, 'add')}
        roleList={roleList || []}
      />
    </div>
  );
};
export default Member;
